import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTenant } from '../context/TenantContext';
import apiService from '../services/api.service';
import { CheckCircle } from 'lucide-react';
import Loader from '../components/ui/loader';

const NPSSurvey = () => {
  const { t, languageContent } = useLanguage();
  const { tenant } = useTenant();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const euid = searchParams.get('euid');

  // Scale configuration matching FeedbackModal exactly
  const scale = [
    { value: 0, label: '0', color: 'bg-red-800 text-white border-red-800', selected: 'bg-red-500 text-white border-red-500' },
    { value: 1, label: '1', color: 'bg-[#E95223] text-white', selected: 'bg-red-500 text-white border-red-500' },
    { value: 2, label: '2', color: 'bg-[#EA6F22] text-white', selected: 'bg-orange-500 text-white border-orange-400' },
    { value: 3, label: '3', color: 'bg-[#FDC729] text-white', selected: 'bg-yellow-500 text-gray-700 border-yellow-500' },
    { value: 4, label: '4', color: 'bg-[#C2D234] text-white', selected: 'bg-lime-500 text-white border-lime-500' },
    { value: 5, label: '5', color: 'bg-[#66B44E] text-white', selected: 'bg-green-500 text-white border-green-500' },
  ];

  // Default improvement reasons matching FeedbackModal
  const defaultReasons = [
    'Ease of Use',
    'App Performance',
    'Feature Satisfaction',
    'Reward Experience',
    'Security',
    'Design & Layout',
    'Overall Experience',
    'Customer Support',
  ];
  
  const [rating, setRating] = useState(null);
  const [hoverRating, setHoverRating] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);

  // NPS text from language content - same keys as FeedbackModal
  const npsCopy = languageContent?.nps || {};

  const npsText = {
    title: npsCopy.title || 'We\'d love your feedback',
    subtitle: npsCopy.subtitle || 'How satisfied are you with this experience?',
    notSatisfied: npsCopy?.scale?.notSatisfied || 'Not satisfied',
    extremelySatisfied: npsCopy?.scale?.extremelySatisfied || 'Extremely satisfied',
    improvementQuestion: npsCopy.improvementQuestion || 'What area do you think needs improvement?',
    commentPlaceholder: npsCopy.commentPlaceholder || 'What area do you think needs improvement?',
    submitButton: npsCopy.submitButton || 'Submit Feedback',
    thankYouTitle: npsCopy.thankYouTitle || 'Thank you for your feedback!',
    thankYouMessage: npsCopy.thankYouMessage || 'Your response has been recorded. We appreciate your input.',
  };

  // Get improvement reasons from language content or use defaults
  const localeReasons = Array.isArray(npsCopy.reasonsList)
    ? npsCopy.reasonsList
    : Array.isArray(npsCopy.reasons)
      ? npsCopy.reasons
      : Object.values(npsCopy.reasons || {});

  const improvementReasons = localeReasons.length ? localeReasons : defaultReasons;

  // Validate euid and fetch client data on mount
  useEffect(() => {
    const validateEuid = async () => {
      if (!euid) {
        setError('Invalid or missing survey link.');
        setLoading(false);
        return;
      }

      try {
        // Validate euid and get client data
        const response = await apiService.get('/api/nps/validate-euid', {
          params: { euid }
        });

        if (response.valid) {
          setClientData(response.client);
        } else {
          setError('This survey link is invalid or has expired.');
        }
      } catch (err) {
        console.error('EUID validation failed:', err);
        setError('Unable to load survey. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    validateEuid();
  }, [euid]);

  const handleRatingClick = async (value) => {
    setRating(value);
    
    // Auto-submit for ratings 3-5
    if (value >= 3) {
      await handleSubmit(value, '', []);
    }
  };

  const toggleReason = (reason) => {
    setSelectedReasons(prev =>
      prev.includes(reason)
        ? prev.filter(r => r !== reason)
        : [...prev, reason]
    );
  };

  const handleSubmit = async (ratingValue = rating, feedbackValue = feedback, reasonsValue = selectedReasons) => {
    if (ratingValue === null) {
      setError('Please select a rating');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Combine selected reasons with feedback text
      const combinedFeedback = reasonsValue.length > 0 
        ? `${reasonsValue.join(', ')}${feedbackValue ? ': ' + feedbackValue : ''}`
        : feedbackValue;

      await apiService.post('/api/nps/submit-response-email', {
        euid,
        rating: ratingValue,
        feedback: combinedFeedback,
        trigger_type: 'email_cashback',
        channel: 'EMAIL'
      });

      setSubmitted(true);
    } catch (err) {
      console.error('NPS submission failed:', err);
      setError(err.response?.data?.message || 'Failed to submit survey. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader 
          label="Loading survey..." 
          size="xl"
          labelColor="text-gray-600"
        />
      </div>
    );
  }

  if (error && !clientData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Survey Unavailable</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const handleClose = () => {
      // Extract tenantUrlName from current path (/:tenanturlname/nps-survey)
      const pathSegments = window.location.pathname.split('/').filter(Boolean);
      const tenantUrlName = pathSegments[0];
      
      // Navigate to tenant home page
      if (tenantUrlName) {
        navigate(`/${tenantUrlName}`);
      } else {
        navigate('/');
      }
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {npsText.thankYouTitle}
          </h2>
          <p className="text-gray-600 mb-6">
            {npsText.thankYouMessage}
          </p>
          <button
            onClick={handleClose}
            className="px-8 py-3 bg-theme-primary text-white rounded-lg font-semibold hover:bg-theme-primary-dark transition-colors shadow-md hover:shadow-lg"
          >
            OK
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          {tenant?.tenantlogo && (
            <img 
              src={tenant.tenantlogo} 
              alt={tenant.tenantname} 
              className="h-12 mx-auto mb-4"
            />
          )}
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {npsText.title}
          </h1>
          <p className="text-gray-600">
            {npsText.subtitle}
          </p>
        </div>

        {/* Rating Scale - Circular buttons matching FeedbackModal */}
        <div className="mb-8">
          <p className="text-center text-gray-700 mb-6 font-medium text-base">
            {npsText.subtitle}
          </p>
          <div className="flex justify-between gap-1 mb-2">
            {scale.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => handleRatingClick(item.value)}
                disabled={isSubmitting}
                className={`w-[42px] h-[42px] rounded-full flex items-center justify-center text-sm font-semibold border transition-colors duration-150
                  ${rating === item.value ? item.selected : item.color}
                  ${rating === item.value ? 'ring-2 ring-offset-2 ring-blue-400 font-bold' : ''}
                  ${rating !== null && rating !== item.value ? 'opacity-40' : ''}
                  ${isSubmitting ? 'cursor-not-allowed' : 'cursor-pointer'}
                `}
                tabIndex={0}
                aria-pressed={rating === item.value}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between mt-1 mb-4">
            <span className="text-[9px] text-gray-500">
              {npsText.notSatisfied}
            </span>
            <span className="text-[9px] text-gray-500">
              {npsText.extremelySatisfied}
            </span>
          </div>
        </div>

        {/* Feedback Section - Only show for ratings 0-2 */}
        {rating !== null && rating <= 2 && (
          <div className="animate-slideDown">
            <form onSubmit={handleManualSubmit} className="space-y-6">
              {/* Improvement Reasons - Matching FeedbackModal styling */}
              <div>
                <label className="block text-sm font-medium mb-2 text-left leading-tight">
                  {npsText.improvementQuestion}
                </label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {improvementReasons.map((label) => {
                    const isSelected = selectedReasons.includes(label);
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => toggleReason(label)}
                        disabled={isSubmitting}
                        className={`px-4 py-2 rounded-md border text-[9px] font-medium transition-colors
                          ${isSelected ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-gray-500 border-gray-300'}
                          ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                        tabIndex={0}
                        aria-pressed={isSelected}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Additional Comments */}
              <div>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder={npsText.commentPlaceholder}
                  rows={3}
                  disabled={isSubmitting}
                  className="w-full px-4 min-h-[80px] border border-gray-300 rounded-md p-3 text-sm text-gray-600 placeholder-gray-400
                    disabled:opacity-50 disabled:bg-gray-50"
                />
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
                  <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-theme-primary text-white rounded-lg font-semibold 
                  hover:bg-theme-primary-dark transition-all duration-200 
                  disabled:opacity-50 disabled:cursor-not-allowed
                  shadow-md hover:shadow-lg transform hover:-translate-y-0.5
                  disabled:transform-none disabled:shadow-md"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  npsText.submitButton
                )}
              </button>
            </form>
          </div>
        )}

        {/* Auto-submit message for ratings 3-5 */}
        {rating !== null && rating >= 3 && isSubmitting && (
          <div className="text-center animate-fadeIn">
            <Loader 
              label="Submitting your rating..." 
              size="lg"
              labelColor="text-gray-600"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NPSSurvey;
